import type { ApolloClient } from '@apollo/client';
import { useEffect, useReducer } from 'react';
import { useApolloClient } from '@apollo/client';
import { isNil } from 'es-toolkit';
import PropTypes from 'prop-types';

import type { JWTContextUser } from '@savvy/deprecated-shared/api/user-api';
import type {
  AuthProviderProps,
  JWTContextState,
  LoginReturn,
} from '@savvy/deprecated-shared/contexts/jwt-context.context';
import type { LoginData } from '@savvy/deprecated-shared/utils/savvy-auth';
import { apiHost } from '@savvy/deprecated-shared/api/api-host';
import { AuthApi } from '@savvy/deprecated-shared/api/auth-api';
import { CurrentUserQuery, fetchCurrentUser } from '@savvy/deprecated-shared/api/user-api';
import { AuthContext } from '@savvy/deprecated-shared/contexts/jwt-context.context';
import { graphql as tadaGraphql } from '@savvy/deprecated-shared/gql-compat';
import { useInviteTokenHeaders } from '@savvy/deprecated-shared/hooks/use-invite-token-header';
import { useRouter } from '@savvy/deprecated-shared/hooks/use-router';
import { setOnNetworkAuthError } from '@savvy/deprecated-shared/utils/apollo-client';
import { JWT_STORAGE_KEY, LocalStorage } from '@savvy/deprecated-shared/utils/local-storage';
import { isWeb } from '@savvy/deprecated-shared/utils/platform/is-web';
import { removeSalesforceAccessToken } from '@savvy/deprecated-shared/utils/salesforce';
import {
  clearUserInfo,
  errMsgToOtpChannel,
  isExpiredJWT,
  isSharedEmployeeAccount,
} from '@savvy/deprecated-shared/utils/savvy-auth';

const INVITE_TOKEN_USER_QUERY = tadaGraphql(/* GraphQL */ `
  query InviteTokenUser {
    client {
      user {
        id
        firstName
        lastName
        email
        role
      }
    }
  }
`);

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    isAuthenticated: boolean;
    user: JWTContextUser | null;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: JWTContextUser;
  };
};

type UpdateAction = {
  type: 'UPDATE';
  payload: {
    user: JWTContextUser;
  };
};

type ErrorEncounteredAction = {
  type: 'ERROR';
  payload: {
    err?: string;
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type Action = InitializeAction | LoginAction | UpdateAction | ErrorEncounteredAction | LogoutAction;

const initialState: JWTContextState = {
  err: undefined,
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

function reducer(state: JWTContextState, action: Action): JWTContextState {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        ...state,
        ...action.payload,
        isInitialized: true,
      };
    case 'LOGIN':
    case 'UPDATE':
      return {
        ...state,
        ...action.payload,
        isAuthenticated: true,
      };
    case 'ERROR':
      return {
        ...state,
        ...action.payload,
        isAuthenticated: false,
        user: null,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      };
  }
}

const LOGOUT_CLEARED_COOKIE_STATUSES = new Set([200, 401]);

async function clearApiSessionCookie(): Promise<void> {
  const response = await fetch(`${apiHost}/logout`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal: AbortSignal.timeout(5000),
  });
  if (!LOGOUT_CLEARED_COOKIE_STATUSES.has(response.status)) {
    throw new Error(`Clearing the API session cookie returned ${response.status}`);
  }
}

export function AuthProvider(props: AuthProviderProps) {
  const { children, inviteToken } = props;
  const router = useRouter();
  const [state, dispatch] = useReducer(reducer, initialState);
  const apolloClient: ApolloClient<object> = useApolloClient();
  const authApi = new AuthApi(apolloClient);
  const headers = useInviteTokenHeaders();

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        const accessToken = await LocalStorage.getItem(JWT_STORAGE_KEY);

        if (accessToken && !isExpiredJWT(accessToken)) {
          const user = await authApi.me(accessToken);

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
            },
          });
        } else if (inviteToken) {
          const user = await apolloClient.query({
            query: INVITE_TOKEN_USER_QUERY,
            context: { headers },
          });

          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: user.data.client.user,
            },
          });
        } else {
          if (isExpiredJWT(accessToken)) {
            clearUserInfo(apolloClient);
          }
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error('JWT / Auth Error', err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    void initialize();
  }, [inviteToken]);

  useEffect(() => {
    setOnNetworkAuthError(() => {
      setOnNetworkAuthError(null);
      clearUserInfo(apolloClient);
      authApi.reset();
      dispatch({ type: 'LOGOUT' });
      if (isWeb()) {
        router.push('/login');
      } else {
        router.replace('/');
      }
    });
    return () => setOnNetworkAuthError(null);
  }, [apolloClient, router, state.isAuthenticated]);

  const login = async (data: LoginData): Promise<LoginReturn> => {
    const { err, jwt } = await authApi.login(data);
    // An OTP challenge is the first half of a login, not a failure. Clearing `user` for it makes
    // _app swap to its signed-out tree and remount the page, discarding the screen that is about
    // to collect the code — an invite-token flow holds a `user` while not yet authenticated.
    if (!isNil(err) && !isNil(errMsgToOtpChannel[err])) {
      return { loginErr: err };
    }
    if (!isNil(err)) {
      dispatch({
        type: 'ERROR',
        payload: {
          err,
        },
      });
    } else {
      await LocalStorage.setItem(JWT_STORAGE_KEY, jwt);
      const user = await authApi.me(jwt);

      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
    }
    return { loginErr: err };
  };
  const update = (user: JWTContextUser) => {
    dispatch({
      type: 'UPDATE',
      payload: {
        user,
      },
    });
    return Promise.resolve();
  };

  const logout = async () => {
    // Probe only to honor the shared-account carve-out below. On failure, assume shared
    // (skip revoke): a failed probe usually means the session is already invalid — its
    // token is dead anyway — and a transient error must not risk revoking a shared account
    // and logging everyone out. Teardown + redirect below run regardless.
    let isSharedAccount = true;
    try {
      const currentUserData = await apolloClient.query({ query: CurrentUserQuery });
      isSharedAccount = isSharedEmployeeAccount(currentUserData.data.currentUser.user);
    } catch (err) {
      console.error('logout: current-user probe failed', err);
    }
    if (!isSharedAccount) {
      // Don't invalidate the jwt for shared users — it would log out everyone on the account.
      try {
        await authApi.logout();
      } catch (err) {
        // Best-effort revoke — swallow so teardown + redirect still run. authApi.logout()
        // uses fetch, which rejects only on network failure (not HTTP 4xx/5xx).
        console.error('logout: token revocation failed', err);
      }
    }
    if (isWeb()) {
      try {
        await clearApiSessionCookie();
      } catch (err) {
        console.error('logout: clearing the API session cookie failed', err);
      }
    }
    clearUserInfo(apolloClient);
    removeSalesforceAccessToken();
    authApi.reset();
    if (isWeb()) {
      // eslint-disable-next-line @typescript-eslint/await-thenable, @typescript-eslint/no-confusing-void-expression -- FIXME: This rule was recently enabled for the repo
      await router.push('/login');
    } else {
      dispatch({
        type: 'LOGOUT',
      }); // @jamison: I believe this breaks on web, but it's necessary for mobile.
      // eslint-disable-next-line @typescript-eslint/await-thenable, @typescript-eslint/no-confusing-void-expression -- FIXME: This rule was recently enabled for the repo
      await router.replace('/');
    }
    return true;
  };

  const reauth = async (): Promise<void> => {
    try {
      await fetchCurrentUser(apolloClient, true);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- FIXME: This rule was recently enabled for the repo
    } catch (error) {
      logout();
      if (isWeb()) {
        // eslint-disable-next-line @typescript-eslint/await-thenable, @typescript-eslint/no-confusing-void-expression -- FIXME: This rule was recently enabled for the repo
        await router.push('/login');
      } else {
        // eslint-disable-next-line @typescript-eslint/await-thenable, @typescript-eslint/no-confusing-void-expression -- FIXME: This rule was recently enabled for the repo
        await router.replace('/');
      }
    }
  };

  return (
    <AuthContext.Provider
      // TODO: Every re-render of one consumer will cause a re-render of all consumers.
      //       We should memoize the value prop and it's dependencies to prevent this.

      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        ...state,
        platform: 'JWT',
        login,
        update,
        logout,
        reauth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
